<script setup lang="ts">
import { computed, ref } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import SearchSuggestionsInput from '@/components/search/search-suggestions-input-next.vue';
import {
  SEARCH_MAX_LENGTH,
  useKeywordSearchQuery,
} from '@/utils/composables/useKeywordSearchQuery';
import { useStore } from '@/utils/vue';

const store = useStore();
const teamInfo = computed(() => store.state.teamInfo.teamInfo);
const enableUserDocument = computed(
  () => teamInfo.value?.enable_user_document ?? false,
);

const { query, search, clear } = useKeywordSearchQuery();

const inputRef = ref<HTMLInputElement>();

const placeholder = computed(() =>
  enableUserDocument.value
    ? 'ニュース・論文・特許・レポート・社内資料などを探そう'
    : 'ニュース・論文・特許・レポートなどを探そう',
);

const deleteHistory = () => inputRef.value?.focus();

const queryEmpty = computed(() => query.value.trim() === '');
const queryTooLong = computed(
  () => query.value.trim().length >= SEARCH_MAX_LENGTH,
);
const buttonsVisible = computed(() => !queryEmpty.value);
const queryValid = computed(() => !queryEmpty.value && !queryTooLong.value);
</script>

<template>
  <SearchSuggestionsInput
    v-model="query"
    @search="search"
    @delete-history="deleteHistory"
  >
    <template #default="{ value, handlers, expanded }">
      <div
        class="keyword-search-bar"
        :class="{
          'too-long': queryTooLong,
          expanded,
        }"
      >
        <DgrIcon
          name="search"
          size="small"
          :keep-fill="false"
          class="search-icon"
        />
        <input
          ref="inputRef"
          class="c-textInput"
          type="text"
          :placeholder="placeholder"
          :value="value"
          v-on="handlers"
          :maxlength="SEARCH_MAX_LENGTH"
          v-tooltip.bottom="
            queryTooLong ? '200文字以内で入力してください' : undefined
          "
        />
        <button
          v-if="buttonsVisible"
          class="clear-search-button"
          @click="clear"
        >
          <DgrIcon
            name="times"
            size="xs"
            :keep-fill="false"
            class="clear-search-icon"
          />
        </button>
        <button
          v-if="buttonsVisible"
          class="c-btn c-btn--auto c-btn--small c-btn--AnewsPrimary search-button"
          :disabled="!queryValid"
          @click="search"
        >
          検索
        </button>
      </div>
    </template>
  </SearchSuggestionsInput>
</template>
<style lang="scss" scoped>
.keyword-search-bar {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 590px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid $color-gray400;
  background: #ffffff;

  &.too-long {
    border-color: $color-orange1000;
  }

  &.expanded {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .c-textInput {
    flex-grow: 1;
    width: unset;
    outline: none;
    border: none;
    padding: 0;
    margin: 0;
  }

  .search-icon,
  .clear-search-icon {
    flex-shrink: 0;
    color: $color-gray600;
  }

  .clear-search-button {
    width: 24px;
    height: 24px;
    padding: 4px;
    border-radius: 50%;
    background: #ffffff;
    border: none;

    &:hover {
      background: $color-gray200;
    }
  }

  .search-button:disabled {
    background: $color-gray400;
  }
}
</style>
